import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import {
  IconCalendarWeekFilled,
  IconChevronLeft,
  IconEdit,
  IconInfoCircle,
  IconPlus,
  IconShare3,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import MDialog from "@/components/ui/atoms/MDialog";
import { MButton } from "@/components/ui/atoms/MButtons";
import MInfiniteScroll from "@/components/ui/atoms/MInfiniteScroll";
import MDatePicker from "@/components/ui/molecules/MDatePicker";
import MLoader from "@/components/ui/atoms/MLoader";
import MOptionsMenu, {
  DropdownOption,
} from "@/components/ui/atoms/MOptionsMenu";
import DrawerPortal from "@/components/ui/atoms/DrawerPortal";
import { getMemberInfo } from "@/hooks/useMembers";
import { useVitalsReadings } from "@/hooks/useVitalsReadings";
import { useVitalsTracking } from "@/hooks/useVitalsTracking";
import { VitalsParameter, VitalsRange, VitalsReading } from "@/types/vitals";

import vitalsTrackingService from "@/services/vitals-tracking.service";
import { triggerNativeVitalsShare } from "@/utils/nativeBridge";
import AddHeightModal from "./AddHeightModal";
import AddReadingModal from "./AddReadingModal";
import EditRangeModal from "./EditRangeModal";
import styles from "./VitalsReadingsDetail.module.css";

// ── Constants ─────────────────────────────────────────────────────────────────

const GLUCOSE_CATEGORIES = [
  "Fasting",
  "Post-prandial",
  "Random",
  "Before bed",
] as const;

const CONTEXT_LABELS: Record<string, string> = {
  AFTER_WALK: "After exercise",
  BEFORE_SLEEP: "Before sleep",
  MISSED_MEDICATION: "Missed medicine",
  FEELING_UNWELL: "Feeling unwell",
  AFTER_MEDICATION: "After medicine",
  RESTING: "Resting",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function isSysOOR(v: number | undefined, r: [number, number] | undefined) {
  return !!r && v != null && (v < r[0] || v > r[1]);
}
function isDiaOOR(v: number | undefined, r: [number, number] | undefined) {
  return !!r && v != null && (v < r[0] || v > r[1]);
}
function isValOOR(v: number | undefined, r: [number, number] | undefined) {
  return !!r && v != null && (v < r[0] || v > r[1]);
}

const GLUCOSE_CAT_RANGE_KEY: Record<string, keyof VitalsRange> = {
  Fasting: "fasting",
  "Post-prandial": "postPrandial",
  Random: "random",
  "Before bed": "beforeBed",
};

const GLUCOSE_DEFAULT_RANGES: Partial<
  Record<keyof VitalsRange, [number, number]>
> = {
  fasting: [70, 99],
  postPrandial: [70, 139],
  random: [70, 139],
  beforeBed: [80, 140],
};

function glucoseRange(
  reading: VitalsReading,
  range: VitalsRange
): [number, number] | undefined {
  const key = reading.values.category
    ? GLUCOSE_CAT_RANGE_KEY[reading.values.category as string]
    : undefined;
  if (!key) return undefined;
  const stored = range[key] as [number, number] | undefined;
  if (Array.isArray(stored) && stored.length >= 2) return stored;
  return GLUCOSE_DEFAULT_RANGES[key];
}
function isOutOfRange(reading: VitalsReading, range: VitalsRange): boolean {
  if (reading.parameter === VitalsParameter.BP)
    return (
      isSysOOR(reading.values.systolic, range.systolic) ||
      isDiaOOR(reading.values.diastolic, range.diastolic)
    );
  if (reading.parameter === VitalsParameter.GLUCOSE) {
    const catRangeMap: Record<string, keyof VitalsRange> = {
      Fasting: "fasting",
      "Post-prandial": "postPrandial",
      Random: "random",
      "Before bed": "beforeBed",
    };
    const rangeKey = reading.values.category
      ? catRangeMap[reading.values.category as string]
      : undefined;
    return rangeKey
      ? isValOOR(
          reading.values.value,
          range[rangeKey] as [number, number] | undefined
        )
      : false;
  }
  return false;
}

function fmtDate(d: Date | string) {
  return format(new Date(d), "d MMM yyyy, h:mm a");
}

function computeBMI(weightKg: number, heightCm: number): number {
  const m = heightCm / 100;
  return Math.round((weightKg / (m * m)) * 10) / 10;
}

function displayWeight(
  kg: number,
  unit: "kg" | "lb"
): { value: string; unitLabel: string } {
  if (unit === "lb")
    return {
      value: String(Math.round(kg * 2.2046 * 10) / 10),
      unitLabel: "lb",
    };
  return { value: String(Math.round(kg * 10) / 10), unitLabel: "kg" };
}

function displayHeightCm(cm: number): string {
  return `${cm} cm`;
}

function getContextLabel(r: VitalsReading): string | null {
  if (r.parameter === VitalsParameter.BP && r.context)
    return CONTEXT_LABELS[r.context] ?? r.context;
  if (r.parameter === VitalsParameter.GLUCOSE && r.values.category) {
    const cat = r.values.category as string;
    return cat;
  }
  return null;
}

// ── Chart data ────────────────────────────────────────────────────────────────

type ChartPt = {
  dateLabel: string;
  fullDate: string;
  systolic?: number;
  diastolic?: number;
  value?: number;
  bmi?: number;
  pulse?: number;
  id: string;
  sysOOR: boolean;
  diaOOR: boolean;
  valOOR: boolean;
};

function toPoints(
  readings: VitalsReading[],
  range: VitalsRange,
  heightCm?: number
): ChartPt[] {
  return [...readings]
    .sort(
      (a, b) =>
        new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime()
    )
    .map((r) => {
      const bmi =
        r.values.bmi != null
          ? r.values.bmi
          : heightCm && r.values.value != null
          ? computeBMI(r.values.value, heightCm)
          : undefined;
      return {
        dateLabel: format(new Date(r.recordedAt), "d MMM"),
        fullDate: format(new Date(r.recordedAt), "d MMM, h:mm a"),
        systolic: r.values.systolic,
        diastolic: r.values.diastolic,
        value: r.values.value,
        bmi,
        pulse: r.values.pulse,
        id: r.id,
        sysOOR: isSysOOR(r.values.systolic, range.systolic),
        diaOOR: isDiaOOR(r.values.diastolic, range.diastolic),
        valOOR: isValOOR(r.values.value, glucoseRange(r, range)),
      };
    });
}

// ── Custom chart dots ─────────────────────────────────────────────────────────

const DOT_OOR = "#D64561";
const DOT_SYS = "#8764BB";
const DOT_DIA = "#468BE5";
const DOT_VAL = "#8764BB";
const LINE_SYS = "#7046AE";
const LINE_DIA = "#468BE5";

const makeSysDot = (onSelect: (pt: ChartPt) => void) =>
  function SysDot(props: any) {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return <g />;
    const handleDown = (e: React.PointerEvent) => {
      e.stopPropagation();
      onSelect(payload);
    };
    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={14}
          fill="transparent"
          onPointerDown={handleDown}
        />
        <circle
          cx={cx}
          cy={cy}
          r={5}
          fill={payload.sysOOR ? DOT_OOR : DOT_SYS}
          stroke="#fff"
          strokeWidth={2}
          onPointerDown={handleDown}
        />
      </g>
    );
  };

const makeDiaDot = (onSelect: (pt: ChartPt) => void) =>
  function DiaDot(props: any) {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return <g />;
    const handleDown = (e: React.PointerEvent) => {
      e.stopPropagation();
      onSelect(payload);
    };
    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={14}
          fill="transparent"
          onPointerDown={handleDown}
        />
        <circle
          cx={cx}
          cy={cy}
          r={5}
          fill={payload.diaOOR ? DOT_OOR : DOT_DIA}
          stroke="#fff"
          strokeWidth={2}
          onPointerDown={handleDown}
        />
      </g>
    );
  };

const makeValDot = (color: string, onSelect: (pt: ChartPt) => void) =>
  function ValDot(props: any) {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return <g />;
    const handleDown = (e: React.PointerEvent) => {
      e.stopPropagation();
      onSelect(payload);
    };
    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={14}
          fill="transparent"
          onPointerDown={handleDown}
        />
        <circle
          cx={cx}
          cy={cy}
          r={5}
          fill={payload.valOOR ? DOT_OOR : color}
          stroke="#fff"
          strokeWidth={2}
          onPointerDown={handleDown}
        />
      </g>
    );
  };

// ── Tooltip pill ──────────────────────────────────────────────────────────────

const TooltipPill: React.FC<{ date: string; value: string }> = ({
  date,
  value,
}) => (
  <div className={styles.chartTooltip}>
    <span className={styles.ttDate}>{date}</span>
    <span className={styles.ttBullet}>•</span>
    <span className={styles.ttVal}>{value}</span>
  </div>
);

// ── Chart scroll wrapper ──────────────────────────────────────────────────────

const PT_WIDTH = 64; // px per data point — must match data.length * 64 in chart panels
const PAGE_READINGS = 6; // readings scrolled per arrow tap

const ChartScrollWrapper: React.FC<{
  children: React.ReactNode;
  dataLength: number;
  hasMore?: boolean;
  onLoadMore?: () => Promise<void>;
}> = ({ children, dataLength, hasMore, onLoadMore }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasLeft, setHasLeft] = useState(false);
  const [isLocallyLoading, setIsLocallyLoading] = useState(false);
  const isAutoScrolling = useRef(false);
  const hasInitialized = useRef(false);
  const prevScrollWidth = useRef(0);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const added = el.scrollWidth - prevScrollWidth.current;
    if (!hasInitialized.current) {
      if (el.scrollWidth > el.clientWidth) {
        setHasLeft(true);
        isAutoScrolling.current = true;
        el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
        setTimeout(() => {
          isAutoScrolling.current = false;
        }, 800);
        hasInitialized.current = true;
      }
    } else if (added > 0) {
      if (el.scrollLeft < 60) {
        // Already at the left edge — new data renders to our left, just stay here and show it
        setHasLeft(false);
      } else {
        // Instant correction keeps existing data visually stable (no shake)
        el.style.scrollBehavior = "auto";
        el.scrollLeft += added;
        el.style.scrollBehavior = "";
        // Then glide left to reveal the newly loaded data
        const target = Math.max(0, el.scrollLeft - PAGE_READINGS * PT_WIDTH);
        isAutoScrolling.current = true;
        requestAnimationFrame(() => {
          el.scrollTo({ left: target, behavior: "smooth" });
          setTimeout(() => {
            isAutoScrolling.current = false;
            const cur = scrollRef.current;
            if (cur) setHasLeft(cur.scrollLeft > 4);
          }, 700);
        });
      }
    } else if (added < 0) {
      // Data was reset (refresh after add/edit/delete) — snap to newest
      el.scrollLeft = el.scrollWidth;
      setHasLeft(el.scrollWidth > el.clientWidth);
    }
    prevScrollWidth.current = el.scrollWidth;
  }, [dataLength]);

  const triggerLoadMore = useCallback(async () => {
    if (isLocallyLoading || !hasMore || !onLoadMore) return;
    setIsLocallyLoading(true);
    try {
      await onLoadMore();
    } finally {
      setIsLocallyLoading(false);
    }
  }, [isLocallyLoading, hasMore, onLoadMore]);

  const handleScroll = useCallback(() => {
    if (isAutoScrolling.current) return;
    const el = scrollRef.current;
    if (!el) return;
    setHasLeft(el.scrollLeft > 4);
    if (el.scrollLeft < 60) {
      triggerLoadMore();
    }
  }, [triggerLoadMore]);

  const handleArrowClick = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollLeft < 60) {
      triggerLoadMore();
      return;
    }
    // Guard scroll so handleScroll doesn't fire loadMore mid-animation
    isAutoScrolling.current = true;
    el.scrollTo({
      left: Math.max(0, el.scrollLeft - PAGE_READINGS * PT_WIDTH),
      behavior: "smooth",
    });
    setTimeout(() => {
      isAutoScrolling.current = false;
      const cur = scrollRef.current;
      if (!cur) return;
      setHasLeft(cur.scrollLeft > 4);
      // Trigger load only after scroll animation has fully settled
      if (cur.scrollLeft < 60) triggerLoadMore();
    }, 700);
  }, [triggerLoadMore]);

  const showArrow = hasLeft || (!!hasMore && !isLocallyLoading);

  return (
    <div className={styles.chartScrollWrapper}>
      <div
        ref={scrollRef}
        className={styles.chartScroll}
        onScroll={handleScroll}
      >
        {children}
      </div>
      {isLocallyLoading && (
        <div className={styles.chartLoadingMore}>
          <MLoader />
        </div>
      )}
      {showArrow && (
        <button
          className={styles.chartLeftIndicator}
          onClick={handleArrowClick}
          type="button"
          aria-label="Scroll to older readings"
        >
          <IconChevronLeft size={16} color="#7046ae" />
        </button>
      )}
    </div>
  );
};

// ── Chart panels ──────────────────────────────────────────────────────────────

interface ChartPanelPassThroughProps {
  hasMore?: boolean;
  onLoadMore?: () => Promise<void>;
}

const BPChartPanel: React.FC<
  { data: ChartPt[]; range: VitalsRange } & ChartPanelPassThroughProps
> = ({ data, range, hasMore, onLoadMore }) => {
  const w = Math.max(300, data.length * 64);
  const latestPt = data[data.length - 1] ?? null;
  const [activePt, setActivePt] = useState<ChartPt | null>(latestPt);
  const SysDot = useMemo(() => makeSysDot(setActivePt), []);
  const DiaDot = useMemo(() => makeDiaDot(setActivePt), []);
  return (
    <>
      <div className={styles.chartTopRow}>
        <span className={styles.chartUnit}>mmHg</span>
        {activePt && (
          <TooltipPill
            date={activePt.fullDate}
            value={`${activePt.systolic}/${activePt.diastolic} mmHg${
              activePt.pulse ? ` · ${activePt.pulse} bpm` : ""
            }`}
          />
        )}
      </div>
      <ChartScrollWrapper
        dataLength={data.length}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
      >
        <LineChart
          width={w}
          height={220}
          data={data}
          margin={{ top: 8, right: 12, left: 0, bottom: 28 }}
          onMouseMove={(d: any) => {
            if (d?.activePayload?.length)
              setActivePt(d.activePayload[0].payload);
          }}
          onClick={(d: any) => {
            if (d?.activePayload?.length)
              setActivePt(d.activePayload[0].payload);
          }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#f0f0f0"
          />
          <XAxis
            dataKey="dateLabel"
            tick={{ fontSize: 10, fill: "#606060", fontFamily: "Manrope" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            domain={["auto", "auto"]}
            tick={{ fontSize: 10, fill: "#9ca3af", fontFamily: "Inter" }}
            axisLine={false}
            tickLine={false}
            width={32}
          />
          {range.systolic && (
            <ReferenceArea
              y1={range.systolic[0]}
              y2={range.systolic[1]}
              fill="#c8b7e0"
              fillOpacity={0.4}
              strokeOpacity={0}
            />
          )}
          {range.diastolic && (
            <ReferenceArea
              y1={range.diastolic[0]}
              y2={range.diastolic[1]}
              fill="#6BA2EA"
              fillOpacity={0.2}
              strokeOpacity={0}
            />
          )}
          <Line
            type="linear"
            dataKey="systolic"
            stroke={LINE_SYS}
            strokeWidth={4}
            dot={SysDot}
            activeDot={{ r: 5 }}
            connectNulls={false}
          />
          <Line
            type="linear"
            dataKey="diastolic"
            stroke={LINE_DIA}
            strokeWidth={4}
            dot={DiaDot}
            activeDot={{ r: 5 }}
            connectNulls={false}
          />
          <Tooltip
            content={() => null}
            cursor={{
              stroke: "#36116d",
              strokeWidth: 1,
              strokeDasharray: "4 4",
            }}
          />
        </LineChart>
      </ChartScrollWrapper>
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: DOT_SYS }} />
          Systolic
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: DOT_DIA }} />
          Diastolic
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: DOT_OOR }} />
          Out of range
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendSwatch} />
          Sys. range
        </span>
        <span className={styles.legendItem}>
          <span
            className={styles.legendSwatch}
            style={{ background: "#6BA2EA", opacity: 0.5 }}
          />
          Dia. range
        </span>
      </div>
    </>
  );
};

const SingleChartPanel: React.FC<
  {
    data: ChartPt[];
    range?: [number, number];
    unit: string;
    label: string;
    dataKey?: "value" | "bmi";
    lineColor?: string;
  } & ChartPanelPassThroughProps
> = ({
  data,
  range,
  unit,
  label,
  dataKey = "value",
  lineColor = LINE_SYS,
  hasMore,
  onLoadMore,
}) => {
  const w = Math.max(300, data.length * 64);
  const latestPt = data[data.length - 1] ?? null;
  const [activePt, setActivePt] = useState<ChartPt | null>(latestPt);
  const valDot = useMemo(
    () => makeValDot(lineColor === LINE_SYS ? DOT_VAL : lineColor, setActivePt),
    [lineColor]
  );
  return (
    <>
      <div className={styles.chartTopRow}>
        <span className={styles.chartUnit}>{unit}</span>
        {activePt && (
          <TooltipPill
            date={activePt.fullDate}
            value={`${activePt[dataKey as keyof ChartPt]} ${unit}`}
          />
        )}
      </div>
      <ChartScrollWrapper
        dataLength={data.length}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
      >
        <LineChart
          width={w}
          height={220}
          data={data}
          margin={{ top: 8, right: 12, left: 0, bottom: 28 }}
          onMouseMove={(d: any) => {
            if (d?.activePayload?.length)
              setActivePt(d.activePayload[0].payload);
          }}
          onClick={(d: any) => {
            if (d?.activePayload?.length)
              setActivePt(d.activePayload[0].payload);
          }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#f0f0f0"
          />
          <XAxis
            dataKey="dateLabel"
            tick={{ fontSize: 10, fill: "#606060", fontFamily: "Manrope" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            domain={["auto", "auto"]}
            tick={{ fontSize: 10, fill: "#9ca3af", fontFamily: "Inter" }}
            axisLine={false}
            tickLine={false}
            width={32}
          />
          {range && (
            <ReferenceArea
              y1={range[0]}
              y2={range[1]}
              fill="#c8b7e0"
              fillOpacity={0.4}
              strokeOpacity={0}
            />
          )}
          <Line
            type="linear"
            dataKey={dataKey}
            stroke={lineColor}
            strokeWidth={4}
            dot={valDot}
            activeDot={{ r: 5 }}
            connectNulls={false}
          />
          <Tooltip
            content={() => null}
            cursor={{
              stroke: "#36116d",
              strokeWidth: 1,
              strokeDasharray: "4 4",
            }}
          />
        </LineChart>
      </ChartScrollWrapper>
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span
            className={styles.legendDot}
            style={{ background: lineColor === LINE_SYS ? DOT_VAL : lineColor }}
          />
          {label}
        </span>
        {dataKey === "value" && (
          <span className={styles.legendItem}>
            <span
              className={styles.legendDot}
              style={{ background: DOT_OOR }}
            />
            Out of range
          </span>
        )}
        {range && (
          <span className={styles.legendItem}>
            <span className={styles.legendSwatch} />
            Ref. range
          </span>
        )}
      </div>
    </>
  );
};

// ── Chart view ────────────────────────────────────────────────────────────────

const ChartView: React.FC<
  {
    readings: VitalsReading[];
    range: VitalsRange;
    param: VitalsParameter;
    total: number;
    weightUnit?: "kg" | "lb";
    glucoseFilter?: string | null;
    onBmiInfoClick?: () => void;
  } & ChartPanelPassThroughProps
> = ({
  readings,
  range,
  param,
  total,
  weightUnit = "kg",
  glucoseFilter = null,
  onBmiInfoClick,
  hasMore,
  onLoadMore,
}) => {
  const { t } = useTranslation();
  const heightCm = range.height;

  if (readings.length === 0) {
    return (
      <p className={styles.emptyText}>
        {t("vitalsTracker.readingsDetail.noReadings")}
      </p>
    );
  }

  return (
    <div className={styles.chartSection}>
      <div className={styles.chartHeaderRow}>
        <span className={styles.readingsCount}>{total} readings</span>
        <span className={styles.swipeHint}>
          {t("vitalsTracker.readingsDetail.swipeHint")}
        </span>
      </div>

      {param === VitalsParameter.BP && (
        <div className={styles.chartCard}>
          <BPChartPanel
            data={toPoints(readings, range)}
            range={range}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
          />
        </div>
      )}

      {param === VitalsParameter.GLUCOSE && (
        <div className={styles.glucoseCharts}>
          {(glucoseFilter ? [glucoseFilter] : GLUCOSE_CATEGORIES).map((cat) => {
            const catReadings = readings.filter(
              (r) => r.values.category === cat
            );
            return (
              <div key={cat} className={styles.chartCard}>
                <div className={styles.chartSectionHeader}>
                  <span className={styles.chartSectionTitle}>{cat}</span>
                  {catReadings.length > 0 && (
                    <span className={styles.chartSectionCount}>
                      {catReadings.length} readings
                    </span>
                  )}
                </div>
                {catReadings.length === 0 ? (
                  <p className={styles.emptyCategory}>
                    {t("vitalsTracker.readingsDetail.noContextReadings", {
                      context: cat,
                    })}
                  </p>
                ) : (
                  <SingleChartPanel
                    data={toPoints(catReadings, range)}
                    range={
                      range[GLUCOSE_CAT_RANGE_KEY[cat] as keyof VitalsRange] as
                        | [number, number]
                        | undefined
                    }
                    unit="mg/dL"
                    label={cat}
                    hasMore={hasMore}
                    onLoadMore={onLoadMore}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {param === VitalsParameter.HEIGHT && (
        <div className={styles.chartCard}>
          <SingleChartPanel
            data={toPoints(readings, range)}
            range={undefined}
            unit="cm"
            label={t("vitalsTracker.heightVital")}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
          />
        </div>
      )}

      {param === VitalsParameter.WEIGHT &&
        (() => {
          const pts = toPoints(readings, range, heightCm);
          const weightPts =
            weightUnit === "lb"
              ? pts.map((p) => ({
                  ...p,
                  value:
                    p.value != null
                      ? Math.round(p.value * 2.2046 * 10) / 10
                      : undefined,
                }))
              : pts;
          const hasBmi = heightCm != null && pts.some((p) => p.bmi != null);
          return (
            <div className={styles.glucoseCharts}>
              <div className={styles.chartCard}>
                <div className={styles.chartSectionHeader}>
                  <span className={styles.chartSectionTitle}>
                    {t("vitalsTracker.weight")}
                  </span>
                </div>
                <SingleChartPanel
                  data={weightPts}
                  range={undefined}
                  unit={weightUnit}
                  label={t("vitalsTracker.weight")}
                  hasMore={hasMore}
                  onLoadMore={onLoadMore}
                />
              </div>
              {hasBmi && (
                <>
                  <div className={styles.bmiSectionHeader}>
                    <span className={styles.bmiSectionTitle}>BMI</span>
                    <button
                      type="button"
                      className={styles.bmiInfoCircle}
                      onClick={onBmiInfoClick}
                      aria-label="BMI information"
                    >
                      <IconInfoCircle size={12} color="#7046ae" />
                    </button>
                    <span className={styles.readingsCount}>
                      {pts.filter((p) => p.bmi != null).length} readings
                    </span>
                    <span className={styles.bmiSectionSpacer} />
                    <span className={styles.swipeHint}>
                      {t("vitalsTracker.readingsDetail.swipeHint")}
                    </span>
                  </div>
                  <div className={styles.chartCard}>
                    <SingleChartPanel
                      data={pts}
                      range={undefined}
                      unit="BMI"
                      label="BMI"
                      dataKey="bmi"
                      lineColor="#468BE5"
                      hasMore={hasMore}
                      onLoadMore={onLoadMore}
                    />
                  </div>
                </>
              )}
            </div>
          );
        })()}
    </div>
  );
};

// ── Table row ─────────────────────────────────────────────────────────────────

const ReadingRow: React.FC<{
  reading: VitalsReading;
  range: VitalsRange;
  param: VitalsParameter;
  onEdit: (r: VitalsReading) => void;
  onDelete: (r: VitalsReading) => void;
  weightUnit?: "kg" | "lb";
}> = ({ reading, range, param, onEdit, onDelete, weightUnit = "kg" }) => {
  const { t } = useTranslation();
  const sOOR = isSysOOR(reading.values.systolic, range.systolic);
  const dOOR = isDiaOOR(reading.values.diastolic, range.diastolic);
  const vOOR = isValOOR(reading.values.value, glucoseRange(reading, range));
  const ctxLabel = getContextLabel(reading);

  const options: DropdownOption[] = [
    {
      id: "edit",
      label: t("vitalsTracker.readingsDetail.editReading"),
      icon: <IconEdit size={16} />,
      onClick: () => onEdit(reading),
    },
    {
      id: "delete",
      label: t("vitalsTracker.readingsDetail.deleteReading"),
      icon: <IconTrash size={16} />,
      onClick: () => onDelete(reading),
      delete: true,
    },
  ];

  return (
    <div className={styles.tableRow}>
      <div className={styles.rowContent}>
        {/* Date */}
        <span className={styles.rowDate}>{fmtDate(reading.recordedAt)}</span>

        {/* Value + badge */}
        <div className={styles.rowValueLine}>
          {param === VitalsParameter.BP ? (
            <div className={styles.rowValUnit}>
              <span className={styles.rowBpValue}>
                <span style={{ color: sOOR ? "#c33f58" : "#36116d" }}>
                  {reading.values.systolic ?? "--"}
                </span>
                <span style={{ color: "#797979" }}>/</span>
                <span style={{ color: dOOR ? "#c33f58" : "#36116d" }}>
                  {reading.values.diastolic ?? "--"}
                </span>
              </span>
              <span className={styles.rowUnit}>mmHg</span>
            </div>
          ) : param === VitalsParameter.HEIGHT ? (
            <div className={styles.rowValUnit}>
              <span className={styles.rowValue}>
                {reading.values.value ?? "--"}
              </span>
              <span className={styles.rowUnit}>cm</span>
            </div>
          ) : param === VitalsParameter.WEIGHT ? (
            <span className={styles.rowWeightBmi}>
              {(() => {
                const wDisp =
                  reading.values.value != null
                    ? displayWeight(reading.values.value, weightUnit)
                    : null;
                const heightCm = range.height;
                const bmiVal =
                  reading.values.bmi ??
                  (heightCm && reading.values.value != null
                    ? computeBMI(reading.values.value, heightCm)
                    : null);
                return (
                  <>
                    <span className={styles.rowWeightVal}>
                      {wDisp?.value ?? "--"}
                    </span>
                    <span className={styles.rowWeightUnit}>
                      {wDisp?.unitLabel ?? weightUnit}
                    </span>
                    {bmiVal != null && (
                      <>
                        <span className={styles.rowInlineDivider}> | </span>
                        <span className={styles.rowBmiVal}>{bmiVal}</span>
                        <span className={styles.rowBmiLabel}>BMI</span>
                      </>
                    )}
                  </>
                );
              })()}
            </span>
          ) : (
            <div className={styles.rowValUnit}>
              <span
                className={`${styles.rowValue}${
                  vOOR ? ` ${styles.rowValueOOR}` : ""
                }`}
              >
                {reading.values.value ?? "--"}
              </span>
              <span className={styles.rowUnit}>mg/dL</span>
            </div>
          )}
          {ctxLabel && <span className={styles.badge}>{ctxLabel}</span>}
        </div>

        {/* Pulse */}
        {param === VitalsParameter.BP && reading.values.pulse != null && (
          <p className={styles.rowMeta}>
            {reading.values.pulse}{" "}
            <span className={styles.rowMetaUnit}>BPM</span>
          </p>
        )}

        {/* Note */}
        {reading.note && <p className={styles.rowNote}>"{reading.note}"</p>}
      </div>
      <MOptionsMenu options={options} />
    </div>
  );
};

// ── Table view ────────────────────────────────────────────────────────────────

const TableView: React.FC<{
  readings: VitalsReading[];
  range: VitalsRange;
  param: VitalsParameter;
  onEdit: (r: VitalsReading) => void;
  onDelete: (r: VitalsReading) => void;
  weightUnit?: "kg" | "lb";
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}> = ({
  readings,
  range,
  param,
  onEdit,
  onDelete,
  weightUnit = "kg",
  hasMore,
  isLoadingMore,
  onLoadMore,
}) => {
  const { t } = useTranslation();
  const sorted = useMemo(
    () =>
      [...readings].sort(
        (a, b) =>
          new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime()
      ),
    [readings]
  );

  if (sorted.length === 0) {
    return (
      <p className={styles.emptyText}>
        {t("vitalsTracker.readingsDetail.noReadings")}
      </p>
    );
  }

  return (
    <MInfiniteScroll
      data={sorted}
      hasMore={hasMore}
      isLoading={isLoadingMore}
      onLoadMore={onLoadMore}
      className={styles.tableList}
      gap={0}
      renderItem={(r) => (
        <ReadingRow
          key={r.id}
          reading={r}
          range={range}
          param={param}
          onEdit={onEdit}
          onDelete={onDelete}
          weightUnit={weightUnit}
        />
      )}
    />
  );
};

// ── Latest reading card ───────────────────────────────────────────────────────

const LatestReadingCard: React.FC<{
  reading: VitalsReading;
  range: VitalsRange;
  memberName: string;
  onEditRange: () => void;
  weightUnit?: "kg" | "lb";
  onWeightUnitChange?: (u: "kg" | "lb") => void;
  onAddHeight?: () => void;
}> = ({
  reading,
  range,
  memberName,
  onEditRange,
  weightUnit = "kg",
  onWeightUnitChange,
  onAddHeight,
}) => {
  const { t } = useTranslation();
  const sOOR = isSysOOR(reading.values.systolic, range.systolic);
  const dOOR = isDiaOOR(reading.values.diastolic, range.diastolic);
  const glucoseRangeArr = glucoseRange(reading, range);
  const vOOR = isValOOR(reading.values.value, glucoseRangeArr);
  const ctxLabel = getContextLabel(reading);

  const oorLabel = (() => {
    if (
      reading.parameter === VitalsParameter.GLUCOSE &&
      vOOR &&
      glucoseRangeArr &&
      reading.values.value != null
    )
      return reading.values.value < glucoseRangeArr[0]
        ? "Below range"
        : "Above range";
    if (reading.parameter === VitalsParameter.BP && (sOOR || dOOR))
      return "Out of range";
    return null;
  })();

  const rangeNumbers =
    reading.parameter === VitalsParameter.BP &&
    range.systolic?.length >= 2 &&
    range.diastolic?.length >= 2
      ? `${range.systolic[0]}-${range.systolic[1]} / ${range.diastolic[0]}-${range.diastolic[1]}`
      : reading.parameter === VitalsParameter.GLUCOSE && glucoseRangeArr
      ? `${glucoseRangeArr[0]}-${glucoseRangeArr[1]}`
      : null;

  const refLabel =
    reading.parameter === VitalsParameter.GLUCOSE && ctxLabel
      ? `Reference range • ${ctxLabel}`
      : "Reference range";

  const heightCm = range.height;
  const wDisp =
    reading.values.value != null
      ? displayWeight(reading.values.value, weightUnit)
      : null;
  const bmiVal =
    heightCm && reading.values.value != null
      ? computeBMI(reading.values.value, heightCm)
      : reading.values.bmi;

  return (
    <div className={styles.latestCard}>
      {/* Title + date */}
      <div className={styles.latestTitleRow}>
        <span className={styles.latestName}>{memberName}'s latest reading</span>
        <span className={styles.latestDate}>
          {format(new Date(reading.recordedAt), "d MMM yyyy • h:mm a")}
        </span>
      </div>

      <div className={styles.latestValues}>
        {/* BP: big value + divider + BPM */}
        {reading.parameter === VitalsParameter.BP && (
          <div className={styles.bpRow}>
            <div className={styles.bpLeft}>
              <span className={styles.bpBig}>
                <span className={sOOR ? styles.bpOOR : styles.bpNorm}>
                  {reading.values.systolic ?? "--"}
                </span>
                <span className={styles.bpSlash}>/</span>
                <span className={dOOR ? styles.bpOOR : styles.bpNorm}>
                  {reading.values.diastolic ?? "--"}
                </span>
              </span>
              <span className={styles.bpUnit}>mmHg</span>
            </div>
            <div className={styles.bpDivider} />
            <div className={styles.bpmLeft}>
              <span className={styles.bpmVal}>
                {reading.values.pulse ?? "--"}
              </span>
              <span className={styles.bpmLabel}>BPM • Pulse</span>
            </div>
          </div>
        )}

        {/* Height: single value */}
        {reading.parameter === VitalsParameter.HEIGHT && (
          <p className={styles.singleVal}>
            {`${reading.values.value ?? "--"} cm`}
          </p>
        )}

        {/* Glucose: single value */}
        {reading.parameter === VitalsParameter.GLUCOSE && (
          <p
            className={`${styles.singleVal}${
              vOOR ? ` ${styles.singleValOOR}` : ""
            }`}
          >
            {`${reading.values.value ?? "--"} mg/dL`}
          </p>
        )}

        {/* Weight: kg/lb toggle + value + BMI */}
        {reading.parameter === VitalsParameter.WEIGHT && (
          <>
            {/* Unit toggle */}
            <div className={styles.weightUnitToggle}>
              {(["kg", "lb"] as const).map((u) => (
                <button
                  key={u}
                  type="button"
                  className={`${styles.weightUnitBtn}${
                    weightUnit === u ? ` ${styles.weightUnitActive}` : ""
                  }`}
                  onClick={() => onWeightUnitChange?.(u)}
                >
                  {u}
                </button>
              ))}
            </div>

            {/* No height: big value + add height prompt */}
            {!heightCm ? (
              <>
                <p className={styles.weightBig}>
                  {wDisp?.value ?? "--"}
                  <span className={styles.weightUnit}>
                    {" "}
                    {wDisp?.unitLabel ?? weightUnit}
                  </span>
                </p>
                <p className={styles.noHeightText}>
                  {t("vitalsTracker.height.addHeightPrompt")}
                </p>
                <button
                  type="button"
                  className={styles.addHeightBtn}
                  onClick={onAddHeight}
                >
                  {t("vitalsTracker.height.addHeightBtn")}
                </button>
              </>
            ) : (
              <>
                {/* Weight + BMI side by side */}
                <div className={styles.weightBmiRow}>
                  <div className={styles.weightValBlock}>
                    <span className={styles.weightBig}>
                      {wDisp?.value ?? "--"}
                    </span>
                    <span className={styles.weightUnit}>
                      {wDisp?.unitLabel ?? weightUnit}
                    </span>
                  </div>
                  <div className={styles.weightBmiDivider} />
                  <div className={styles.bmiBlock}>
                    <span className={styles.bmiBig}>{bmiVal ?? "--"}</span>
                    <span className={styles.bmiLabel}>
                      {t("vitalsTracker.height.bmiLabel")}
                    </span>
                  </div>
                </div>

                {/* Divider between weight/BMI and height */}
                <div className={styles.weightHeightDivider} />

                {/* Height row with edit */}
                <div className={styles.heightRow}>
                  <span className={styles.heightLabel}>
                    {t("vitalsTracker.height.heightLabel")}
                  </span>
                  <span className={styles.heightValue}>
                    {displayHeightCm(heightCm)}
                  </span>
                  <button
                    type="button"
                    className={styles.heightEditBtn}
                    onClick={onAddHeight}
                    aria-label="Edit height"
                  >
                    <IconEdit size={14} color="#8B3166" />
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* Badges: category + OOR status */}
        {(ctxLabel || oorLabel) && (
          <div className={styles.badgeRow}>
            {ctxLabel && <span className={styles.badge}>{ctxLabel}</span>}
            {oorLabel && <span className={styles.badgeOOR}>{oorLabel}</span>}
          </div>
        )}

        {/* Note */}
        {reading.note && <p className={styles.latestNote}>"{reading.note}"</p>}
      </div>

      {/* Divider + ref range — shown for BP and GLUCOSE only */}
      {reading.parameter !== VitalsParameter.WEIGHT &&
        reading.parameter !== VitalsParameter.HEIGHT && (
          <>
            <div className={styles.latestDivider} />
            <div className={styles.refRow}>
              <div className={styles.refLabelGroup}>
                <span className={styles.refLabel}>{refLabel}</span>
                {rangeNumbers && (
                  <span className={styles.refValue}>{rangeNumbers}</span>
                )}
              </div>
              <button
                className={styles.editRangeBtn}
                type="button"
                aria-label="Edit range"
                onClick={onEditRange}
              >
                <IconEdit size={16} color="#8B3166" />
              </button>
            </div>
          </>
        )}
    </div>
  );
};

// ── Delete dialog ─────────────────────────────────────────────────────────────

const DeleteDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  deleting: boolean;
}> = ({ isOpen, onClose, onConfirm, deleting }) => {
  const { t } = useTranslation();
  return (
    <MDialog
      isOpen={isOpen}
      onClose={onClose}
      title={t("vitalsTracker.readingsDetail.deleteTitle")}
      primaryCtaText={
        deleting
          ? t("common.deleting")
          : t("vitalsTracker.readingsDetail.deleteYes")
      }
      onPrimaryCta={onConfirm}
      isPrimaryCtaDisabled={deleting}
      secondaryCtaText={t("vitalsTracker.readingsDetail.deleteNo")}
      onSecondaryCta={onClose}
    >
      <p className={styles.deleteBody}>
        {t("vitalsTracker.readingsDetail.deleteBody")}
      </p>
    </MDialog>
  );
};

// ── Drawer animation variants ─────────────────────────────────────────────────

const backdropVariants = {
  visible: { opacity: 1, pointerEvents: "auto" as const },
  hidden: { opacity: 0, pointerEvents: "none" as const },
};
const drawerVariants = { hidden: { y: "100%" }, visible: { y: 0 } };

// ── BMI Info Drawer ───────────────────────────────────────────────────────────

const BmiInfoDrawer: React.FC<{ isOpen: boolean; onClose: () => void }> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  return (
    <DrawerPortal>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={styles.backdrop}
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>
      <div className={styles.drawerWrapper}>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className={styles.drawer}
              variants={drawerVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ type: "tween", duration: 0.25 }}
            >
              <div className={styles.dragBar} />
              <div className={styles.drawerHeader}>
                <span className={styles.drawerTitle}>
                  {t("vitalsTracker.readingsDetail.bmiInfo.title")}
                </span>
                <button
                  type="button"
                  className={styles.drawerCloseBtn}
                  onClick={onClose}
                  aria-label="Close"
                >
                  <IconX size={18} />
                </button>
              </div>
              <div className={styles.drawerBody}>
                <p className={styles.bmiInfoText}>
                  {t("vitalsTracker.readingsDetail.bmiInfo.body")}
                </p>
                <p className={styles.bmiInfoText}>
                  {t("vitalsTracker.readingsDetail.bmiInfo.rangesTitle")}
                </p>
                <ul className={styles.bmiRangeList}>
                  <li>{t("vitalsTracker.readingsDetail.bmiInfo.low")}</li>
                  <li>{t("vitalsTracker.readingsDetail.bmiInfo.standard")}</li>
                  <li>
                    {t("vitalsTracker.readingsDetail.bmiInfo.borderline")}
                  </li>
                  <li>{t("vitalsTracker.readingsDetail.bmiInfo.high")}</li>
                </ul>
                <p className={styles.bmiInfoText}>
                  {t("vitalsTracker.readingsDetail.bmiInfo.disclaimer")}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DrawerPortal>
  );
};

// ── Date Range Drawer ─────────────────────────────────────────────────────────

const DateRangeDrawer: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onDownload: (startDate?: Date, endDate?: Date) => void;
  downloading: boolean;
}> = ({ isOpen, onClose, onDownload, downloading }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"all" | "range">("all");
  const [startDateObj, setStartDateObj] = useState<Date | null>(null);
  const [endDateObj, setEndDateObj] = useState<Date | null>(null);
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);

  const formatDisplay = (d: Date | null) =>
    d
      ? d.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "Select date";

  const handleDownload = () => {
    if (mode === "all") {
      onDownload(undefined, undefined);
    } else {
      onDownload(startDateObj ?? undefined, endDateObj ?? undefined);
    }
  };

  return (
    <>
      <DrawerPortal>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className={styles.backdrop}
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              onClick={onClose}
            />
          )}
        </AnimatePresence>
        <div className={styles.drawerWrapper}>
          <AnimatePresence>
            {isOpen && (
              <motion.div
                className={styles.drawer}
                variants={drawerVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                transition={{ type: "tween", duration: 0.25 }}
              >
                <div className={styles.dragBar} />
                <div className={styles.dateRangeHeader}>
                  <h2 className={styles.dateRangeTitle}>
                    {t("vitalsTracker.readingsDetail.dateRangeDrawer.title")}
                  </h2>
                  <p className={styles.dateRangeSubtitle}>
                    {t("vitalsTracker.readingsDetail.dateRangeDrawer.subtitle")}
                  </p>
                </div>
                <div className={styles.dateRangeDivider} />
                <div className={styles.dateRangeBody}>
                  {/* Option 1 — Include all dates */}
                  <button
                    type="button"
                    className={styles.dateRangeOption}
                    onClick={() => setMode("all")}
                  >
                    <span
                      className={`${styles.radioCircle} ${
                        mode === "all" ? styles.radioCircleSelected : ""
                      }`}
                    >
                      {mode === "all" && (
                        <span className={styles.radioCircleInner} />
                      )}
                    </span>
                    <span className={styles.dateRangeOptionLabel}>
                      {t(
                        "vitalsTracker.readingsDetail.dateRangeDrawer.allDates"
                      )}
                    </span>
                  </button>
                  {/* Option 2 — Set date-range */}
                  <button
                    type="button"
                    className={styles.dateRangeOption}
                    onClick={() => setMode("range")}
                  >
                    <span
                      className={`${styles.radioCircle} ${
                        mode === "range" ? styles.radioCircleSelected : ""
                      }`}
                    >
                      {mode === "range" && (
                        <span className={styles.radioCircleInner} />
                      )}
                    </span>
                    <div className={styles.dateRangePickerGroup}>
                      <span className={styles.dateRangeSetLabel}>
                        {t(
                          "vitalsTracker.readingsDetail.dateRangeDrawer.setRange"
                        )}
                      </span>
                      <div className={styles.dateRangePickers}>
                        <button
                          type="button"
                          className={styles.datePickerTrigger}
                          onClick={(e) => {
                            e.stopPropagation();
                            setStartPickerOpen(true);
                            setMode("range");
                          }}
                        >
                          <span className={styles.datePickerText}>
                            {formatDisplay(startDateObj)}
                          </span>
                          <IconCalendarWeekFilled size={16} color="#8764bb" />
                        </button>
                        <span className={styles.dateRangeSep}>-</span>
                        <button
                          type="button"
                          className={styles.datePickerTrigger}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEndPickerOpen(true);
                            setMode("range");
                          }}
                        >
                          <span className={styles.datePickerText}>
                            {formatDisplay(endDateObj)}
                          </span>
                          <IconCalendarWeekFilled size={16} color="#8764bb" />
                        </button>
                      </div>
                    </div>
                  </button>
                </div>
                <div className={styles.dateRangeActions}>
                  <button
                    type="button"
                    className={styles.drCancelBtn}
                    onClick={onClose}
                  >
                    {t("vitalsTracker.readingsDetail.dateRangeDrawer.cancel")}
                  </button>
                  <button
                    type="button"
                    className={styles.drDownloadBtn}
                    onClick={handleDownload}
                    disabled={downloading}
                  >
                    {t("vitalsTracker.readingsDetail.dateRangeDrawer.download")}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DrawerPortal>
      <MDatePicker
        open={startPickerOpen}
        onClose={() => setStartPickerOpen(false)}
        onDone={(d) => {
          setStartDateObj(d);
          setStartPickerOpen(false);
        }}
        initialValue={startDateObj ?? undefined}
        title="Start Date"
        maxDate={endDateObj ?? new Date()}
      />
      <MDatePicker
        open={endPickerOpen}
        onClose={() => setEndPickerOpen(false)}
        onDone={(d) => {
          setEndDateObj(d);
          setEndPickerOpen(false);
        }}
        initialValue={endDateObj ?? undefined}
        title="End Date"
        minDate={startDateObj ?? undefined}
        maxDate={new Date()}
      />
    </>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const VitalsReadingsDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { parameter } = useParams<{ parameter: string }>();
  const param =
    (parameter?.toUpperCase() as VitalsParameter) ?? VitalsParameter.BP;

  const memberInfo = getMemberInfo();
  const memberId = memberInfo?.id ?? "";
  const memberName = memberInfo?.nickname ?? memberInfo?.firstName ?? "Your";

  const {
    config,
    isLoading: configLoading,
    refreshConfig,
  } = useVitalsTracking(memberId);
  const {
    readings,
    total,
    isLoading: readingsLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    refresh,
    deleteReading,
  } = useVitalsReadings(memberId, param);

  const [activeTab, setActiveTab] = useState<"chart" | "table">("chart");

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editReading, setEditReading] = useState<VitalsReading | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<VitalsReading | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editRangeOpen, setEditRangeOpen] = useState(false);
  const [weightUnit, setWeightUnit] = useState<"kg" | "lb">("kg");
  const [addHeightOpen, setAddHeightOpen] = useState(false);
  const [glucoseFilter, setGlucoseFilter] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [bmiInfoOpen, setBmiInfoOpen] = useState(false);
  const [dateRangeOpen, setDateRangeOpen] = useState(false);

  const range = config?.ranges?.[param] ?? {};
  const latestReading = config?.latestReadings?.[param];

  const filteredReadings = useMemo(
    () =>
      glucoseFilter
        ? readings.filter((r) => r.values.category === glucoseFilter)
        : readings,
    [readings, glucoseFilter]
  );

  const title =
    param === VitalsParameter.BP
      ? t("vitalsTracker.bloodPressure")
      : param === VitalsParameter.GLUCOSE
      ? t("vitalsTracker.bloodSugar")
      : param === VitalsParameter.HEIGHT
      ? t("vitalsTracker.heightVital")
      : t("vitalsTracker.weight");

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteReading(deleteTarget.id);
      await refreshConfig();
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, deleteReading, refreshConfig]);

  const handleShare = useCallback(() => {
    setDateRangeOpen(true);
  }, []);

  const handleDownload = useCallback(
    async (startDate?: Date, endDate?: Date) => {
      if (!memberId || sharing) return;
      setDateRangeOpen(false);

      // On native device: hand off to the app with date range params
      const handed = triggerNativeVitalsShare({
        memberId,
        memberName,
        parameter: param,
        parameterName: title,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      if (handed) return;

      // Web fallback: download PDF directly
      setSharing(true);
      try {
        const blob = await vitalsTrackingService.downloadVitalsPdf(
          memberId,
          param,
          startDate,
          endDate
        );
        const filename = `${memberName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")}-${param.toLowerCase()}.pdf`;
        const file = new File([blob], filename, { type: "application/pdf" });
        const navAny = navigator as any;
        if (
          navAny.canShare &&
          navAny.canShare({ files: [file] }) &&
          navigator.share
        ) {
          await navigator.share({ title, files: [file] });
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        }
      } catch {
        // Share cancelled or unsupported — silently ignore
      } finally {
        setSharing(false);
      }
    },
    [memberId, memberName, param, title, sharing]
  );

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <MButton variant="back" onClick={() => navigate(-1)} />
        <h1 className={styles.headerTitle}>{title}</h1>
        <button
          className={styles.shareBtn}
          onClick={handleShare}
          disabled={sharing}
          type="button"
          aria-label="Share"
        >
          <IconShare3 size={18} />
        </button>
      </div>

      {configLoading || readingsLoading ? (
        <div className={styles.loaderWrap}>
          <MLoader />
        </div>
      ) : (
        <div className={styles.content}>
          {latestReading && (
            <LatestReadingCard
              reading={latestReading}
              range={range}
              memberName={memberName}
              onEditRange={() => setEditRangeOpen(true)}
              weightUnit={weightUnit}
              onWeightUnitChange={setWeightUnit}
              onAddHeight={() => setAddHeightOpen(true)}
            />
          )}

          {/* Add reading */}
          <button
            className={styles.addBtn}
            onClick={() =>
              param === VitalsParameter.HEIGHT
                ? setAddHeightOpen(true)
                : setAddModalOpen(true)
            }
            type="button"
          >
            <IconPlus size={16} />
            <span>{t("vitalsTracker.readingsDetail.addReading")}</span>
          </button>

          {/* Tab toggle */}
          <div className={styles.tabContainer}>
            <div className={styles.tabTrack}>
              <button
                className={`${styles.tabItem}${
                  activeTab === "chart" ? ` ${styles.tabActive}` : ""
                }`}
                onClick={() => {
                  setActiveTab("chart");
                  setGlucoseFilter(null);
                }}
                type="button"
              >
                Chart
              </button>
              <button
                className={`${styles.tabItem}${
                  activeTab === "table" ? ` ${styles.tabActive}` : ""
                }`}
                onClick={() => setActiveTab("table")}
                type="button"
              >
                Table
              </button>
            </div>
          </div>

          {/* Glucose category filter chips — table view only */}
          {param === VitalsParameter.GLUCOSE && activeTab === "table" && (
            <div className={styles.filterRow}>
              {(
                [
                  null,
                  "Fasting",
                  "Post-prandial",
                  "Random",
                  "Before bed",
                ] as const
              ).map((cat) => (
                <button
                  key={cat ?? "all"}
                  type="button"
                  className={`${styles.filterChip}${
                    glucoseFilter === cat ? ` ${styles.filterChipActive}` : ""
                  }`}
                  onClick={() => setGlucoseFilter(cat)}
                >
                  {cat === null ? t("common.all") : cat}
                </button>
              ))}
            </div>
          )}

          {/* Content */}
          {activeTab === "chart" ? (
            <ChartView
              readings={filteredReadings}
              range={range}
              param={param}
              total={filteredReadings.length}
              weightUnit={weightUnit}
              glucoseFilter={glucoseFilter}
              onBmiInfoClick={() => setBmiInfoOpen(true)}
              hasMore={hasMore}
              onLoadMore={loadMore}
            />
          ) : (
            <TableView
              readings={filteredReadings}
              range={range}
              param={param}
              onEdit={setEditReading}
              onDelete={setDeleteTarget}
              weightUnit={weightUnit}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
              onLoadMore={loadMore}
            />
          )}
        </div>
      )}

      {addModalOpen && (
        <AddReadingModal
          key="new"
          isOpen
          onClose={() => setAddModalOpen(false)}
          parameter={param}
          memberId={memberId}
          onSuccess={async () => {
            await Promise.all([refresh(), refreshConfig()]);
            setAddModalOpen(false);
          }}
          isFirstTime={false}
        />
      )}
      {editReading && (
        <AddReadingModal
          key={`edit-${editReading.id}`}
          isOpen
          onClose={() => setEditReading(null)}
          parameter={param}
          memberId={memberId}
          onSuccess={async () => {
            await Promise.all([refresh(), refreshConfig()]);
            setEditReading(null);
          }}
          isFirstTime={false}
          editReading={editReading}
        />
      )}
      <DeleteDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        deleting={deleting}
      />
      {editRangeOpen &&
        param !== VitalsParameter.WEIGHT &&
        param !== VitalsParameter.HEIGHT && (
          <EditRangeModal
            isOpen
            onClose={() => setEditRangeOpen(false)}
            parameter={param as VitalsParameter.BP | VitalsParameter.GLUCOSE}
            memberId={memberId}
            currentRange={range}
            onSuccess={async () => {
              await Promise.all([refresh(), refreshConfig()]);
            }}
          />
        )}
      <AddHeightModal
        isOpen={addHeightOpen}
        onClose={() => setAddHeightOpen(false)}
        memberId={memberId}
        currentHeight={
          param === VitalsParameter.HEIGHT ? undefined : range.height
        }
        mode={param === VitalsParameter.HEIGHT ? "reading" : "config"}
        onSuccess={async () => {
          await Promise.all([refresh(), refreshConfig()]);
          setAddHeightOpen(false);
        }}
      />
      <BmiInfoDrawer
        isOpen={bmiInfoOpen}
        onClose={() => setBmiInfoOpen(false)}
      />
      <DateRangeDrawer
        isOpen={dateRangeOpen}
        onClose={() => setDateRangeOpen(false)}
        onDownload={handleDownload}
        downloading={sharing}
      />
    </div>
  );
};

export default VitalsReadingsDetail;
